import { useQuery } from "@tanstack/react-query";
import React, { type ReactNode, useMemo } from "react";
import { ApiError } from "../api/client";
import type { BaseForecastViewNode } from "../api/models/baseForecastView";
import type {
	ForecastDataPointResponseApi,
	ForecastMetricsResponseApi,
} from "../api/models/forecast";
import { getBaseForecastGraphql } from "../api/services/baseForecastGraphqlService";
import { getForecast } from "../api/services/forecastService";
import { generateForecastData } from "../data/demo-data";
import type { ForecastDataPoint, ForecastMetrics } from "../types";
import { useApp } from "./AppContext";
import { BaseForecastContext } from "./BaseForecastContextDef";

function mapGraphqlNodeToForecastDataPoint(
	node: BaseForecastViewNode,
): ForecastDataPoint {
	return {
		date: new Date(node.date),
		openingBalance: node.openingBalance,
		balance: node.balance,
		incoming: node.incoming,
		outgoing: node.outgoing,
		scenario: "base",
		confidenceScore: node.confidenceScore ?? undefined,
	};
}

function mapApiToForecastDataPoint(
	api: ForecastDataPointResponseApi,
): ForecastDataPoint {
	return {
		date: new Date(api.date),
		openingBalance: api.openingBalance,
		balance: api.balance,
		incoming: api.incoming,
		outgoing: api.outgoing,
		scenario: "base",
		confidenceScore: api.confidenceScore,
		contributingLayers: api.contributingLayers,
	};
}

function mapApiToMetrics(api: ForecastMetricsResponseApi): ForecastMetrics {
	return {
		minCash: api.minCash,
		minCashDate: new Date(api.minCashDate),
		endingCash: api.endingCash,
		coverageMonths: api.coverageMonths,
		isNoBurn: api.isNoBurn,
		avgMonthlyInflow: api.avgMonthlyInflow,
		avgMonthlyOutflow: api.avgMonthlyOutflow,
		confidenceScore: api.confidenceScore,
		startingCash: api.startingCash,
	};
}

export const BaseForecastProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { mode } = useApp();
	const isDemo = mode === "demo";

	// Demo forecast — stable reference, computed once
	const demoForecast = useMemo(() => generateForecastData("base"), []);

	// Live forecast via React Query — REST supplies the rich metrics, layer
	// availability and readiness (404) state that the GraphQL view does not carry.
	const {
		data: forecastResponse,
		isLoading: queryLoading,
		error: queryError,
		refetch,
	} = useQuery({
		queryKey: ["forecast", "90d"],
		queryFn: () => getForecast("90d"),
		enabled: !isDemo,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	// Ground-truth base forecast timeline via GraphQL (vw_BaseForecast — latest
	// completed daily run). Preferred as the timeline source in live mode; the
	// REST timeline remains the fallback until the GraphQL points resolve.
	const { data: graphqlBaseForecast } = useQuery({
		queryKey: ["baseForecastGraphql"],
		queryFn: () => getBaseForecastGraphql({ periodKey: { eq: "90d" } }, 400),
		enabled: !isDemo,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	const value = useMemo(() => {
		const graphqlForecast =
			graphqlBaseForecast && graphqlBaseForecast.edges.length > 0
				? graphqlBaseForecast.edges.map((e) =>
						mapGraphqlNodeToForecastDataPoint(e.node),
					)
				: null;
		const liveForecast =
			graphqlForecast ??
			(forecastResponse
				? forecastResponse.timeline.map(mapApiToForecastDataPoint)
				: null);
		const liveMetrics = forecastResponse
			? mapApiToMetrics(forecastResponse.metrics)
			: null;
		const baseForecast = isDemo ? demoForecast : (liveForecast ?? []);
		const forecastNotReady =
			!isDemo && queryError instanceof ApiError && queryError.status === 404;

		return {
			baseForecast,
			isLoading: isDemo ? false : queryLoading,
			error: isDemo
				? null
				: queryError instanceof Error
					? queryError.message
					: queryError
						? "Failed to load base forecast"
						: null,
			forecastNotReady,
			metrics: isDemo ? null : liveMetrics,
			availableLayers: isDemo ? [] : (forecastResponse?.availableLayers ?? []),
			missingLayers: isDemo ? [] : (forecastResponse?.missingLayers ?? []),
			resolution: isDemo ? null : (forecastResponse?.resolution ?? null),
			forecastRunId: isDemo
				? undefined
				: (forecastResponse?.forecastRunId ?? undefined),
			refetch: isDemo
				? () => {}
				: () => {
						refetch();
					},
		};
	}, [
		demoForecast,
		forecastResponse,
		graphqlBaseForecast,
		queryLoading,
		queryError,
		isDemo,
		refetch,
	]);

	return (
		<BaseForecastContext.Provider value={value}>
			{children}
		</BaseForecastContext.Provider>
	);
};
